import { cn } from '@shadcn/lib/utils';
import { Table as ShadcnTable } from '@shadcn/ui/table';
import {
  ColumnDef,
  TableOptions,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { useMemo } from 'react';

import { TABLE_CONSTANTS } from './constants';
import { TablePagination } from './table-subcomponents/pagination/table-pagination';
import { TableBody } from './table-subcomponents/table-body';
import { TableHeader } from './table-subcomponents/table-header';
import { createTableInitialState, createTableState } from './table-utils';
import { TableManipulationOptions } from './types';

interface ITableConfigOptions<TData> {
  columns: ColumnDef<TData>[];
  data: TData[];
  manipulationOptions?: TableManipulationOptions;
  pageSize?: number | null;
}

function createTableConfig<TData>({
  columns,
  data,
  manipulationOptions,
  pageSize,
}: ITableConfigOptions<TData>): TableOptions<TData> {
  const isServerSide = manipulationOptions?.type === 'serverSide';
  const isClientSide = manipulationOptions?.type === 'clientSide';

  return {
    autoResetPageIndex: isClientSide,
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: isClientSide ? getFilteredRowModel() : undefined,
    getPaginationRowModel: isClientSide ? getPaginationRowModel() : undefined,
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: isClientSide ? 'includesString' : undefined,
    initialState: createTableInitialState(manipulationOptions, pageSize ?? undefined),
    manualPagination: isServerSide,
    manualSorting: isServerSide,
    ...(isServerSide ? { onPaginationChange: manipulationOptions.onPaginationChange } : {}),
    ...(isServerSide ? { onSortingChange: manipulationOptions.onSortingChange } : {}),
    rowCount: isServerSide ? manipulationOptions.totalRowCount : undefined,
    state: createTableState(manipulationOptions),
  };
}

interface ITableProps<TData> {
  readonly className?: string;
  readonly columns: ColumnDef<TData>[];
  readonly data: TData[];
  readonly manipulationOptions?: TableManipulationOptions;
  readonly pageSize?: number | null;
}

const Table = <TData,>({ className, columns, data, manipulationOptions, pageSize }: ITableProps<TData>) => {
  const isServerSide = manipulationOptions?.type === 'serverSide';

  const tableConfig = useMemo(
    () =>
      createTableConfig({
        columns,
        data,
        manipulationOptions,
        pageSize,
      }),
    [columns, data, manipulationOptions, pageSize]
  );
  const table = useReactTable(tableConfig);

  const { pageSize: currentPageSize } = table.getState().pagination;

  const pageCount = isServerSide
    ? Math.ceil(manipulationOptions.totalRowCount / currentPageSize)
    : table.getPageCount();

  const loading = isServerSide && manipulationOptions.loading;

  return (
    <div className={cn(TABLE_CONSTANTS.STYLING.BORDERS.TABLE, className)}>
      <ShadcnTable className="shadow-md table-fixed">
        <TableHeader headerGroups={table.getHeaderGroups()} />
        <TableBody columns={columns} loading={loading} rows={table.getRowModel().rows} />
      </ShadcnTable>
      {pageSize !== null && (
        <TablePagination
          currentPageSize={currentPageSize}
          manipulationOptions={isServerSide ? manipulationOptions : undefined}
          pageCount={pageCount}
          table={table}
        />
      )}
    </div>
  );
};
export type { ITableProps };
export { Table };
